import styled from '@emotion/styled';
import dayjs from 'dayjs';
import { HighScoreEntity, SingSetup, Song } from '~/interfaces';
import { Button } from '~/modules/Elements/Button';
import { typography } from '~/modules/Elements/cssMixins';
import styles from '~/modules/GameEngine/Drawing/styles';
import useKeyboardNav from '~/modules/hooks/useKeyboardNav';
import useViewportSize from '~/modules/hooks/useViewportSize';
import ScoreText from '~/routes/Game/Singing/GameOverlay/Components/ScoreText';
import { useEditScore } from '~/modules/Songs/stats/hooks';
import HighScoreRename from './HighScoreRename';

interface Props {
  onNextStep: () => void;
  singSetup: SingSetup;
  highScores: HighScoreEntity[];
  song: Song;
}

function HighScoresView({ onNextStep, highScores, singSetup, song }: Props) {
  const { register } = useKeyboardNav();
  const editScore = useEditScore(song);

  const { width } = useViewportSize();
  const isMobile = (width ?? 9999) < 640;

  return (
    <Wrapper data-mobile={isMobile}>
      <ScoresContainer data-test="highscores-container" data-mobile={isMobile}>
        {highScores.map((score, index) => (
          <ScoreContainer key={index} isCurrentSing={score.singSetupId === singSetup.id} data-mobile={isMobile}>
            <ScorePosition>{index + 1}</ScorePosition>

            <ScorePlayerName className="ph-no-capture">
              {score.singSetupId === singSetup.id ? (
                <HighScoreRename
                  index={index}
                  score={score}
                  register={register}
                  singSetupId={singSetup.id}
                  onSave={editScore}
                />
              ) : (
                score.name
              )}
            </ScorePlayerName>

            <ScoreScore>
              <ScoreText score={score.score} />
            </ScoreScore>

            <ScoreDate data-mobile={isMobile}>{dayjs(score.date).format('MMMM D, YYYY')}</ScoreDate>
          </ScoreContainer>
        ))}
      </ScoresContainer>

      <BottomActions data-mobile={isMobile}>
        <SongSelectionButton {...register('play-next-song-button', onNextStep, undefined, true)} data-mobile={isMobile}>
          Select song
        </SongSelectionButton>
      </BottomActions>
    </Wrapper>
  );
}

export default HighScoresView;

/* -------------------- Styles -------------------- */

const Wrapper = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;

  &[data-mobile='true'] {
    position: absolute;
    inset: 0;
    min-height: 0;

    /* reserva para el sticky de abajo */
    padding-bottom: calc(86px + env(safe-area-inset-bottom));
    box-sizing: border-box;
  }
`;

const ScoresContainer = styled.div`
  position: absolute;
  top: 20rem;
  width: 100%;
  text-align: center;
  padding: 0 16rem;
  box-sizing: border-box;

  /* ✅ Mobile: normal flow + scroll */
  &[data-mobile='true'] {
    position: relative;
    top: auto;
    padding: 0;
    margin-top: 1rem;

    overflow: auto;
    -webkit-overflow-scrolling: touch;

    flex: 1;
    min-height: 0;

    /* espacio extra por si hay sombras/fechas */
    padding: 0 0.25rem 1rem 0.25rem;
  }
`;

const ScoreContainer = styled.div<{ isCurrentSing: boolean }>`
  position: relative;
  ${typography};
  font-size: 3.2rem;
  display: flex;
  background: ${(props) => (props.isCurrentSing ? 'rgba(0,0,0,.9)' : 'rgba(0,0,0,.5)')};
  margin-bottom: 2rem;
  padding: ${(props) => (props.isCurrentSing ? `0 1.5rem` : `1rem 3.2rem`)};
  align-items: center;

  /* ✅ Mobile */
  &[data-mobile='true'] {
    font-size: clamp(14px, 4.2vw, 20px);
    padding: 0.9rem 1rem;
    margin-bottom: 0.75rem;
    border-radius: 14px;
  }
`;

const ScorePosition = styled.div`
  padding: 0 1.6rem;
  color: ${styles.colors.text.active};

  @media (max-width: 640px) {
    padding: 0 0.75rem 0 0.25rem;
  }
`;

const ScorePlayerName = styled.div`
  text-align: left;
  flex: 1;
  padding: 1.6rem;

  @media (max-width: 640px) {
    padding: 0.25rem 0.5rem;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const ScoreScore = styled.div`
  padding: 0 1rem;

  @media (max-width: 640px) {
    padding: 0 0.5rem;
    white-space: nowrap;
  }
`;

const ScoreDate = styled.div`
  position: absolute;
  font-size: 2rem;
  bottom: -1rem;
  right: -1.6rem;
  background: black;
  padding: 0.5rem;

  &[data-mobile='true'] {
    position: relative;
    bottom: auto;
    right: auto;
    margin-left: auto;
    margin-top: 0.35rem;
    font-size: clamp(12px, 3.3vw, 14px);
    opacity: 0.9;
    padding: 0.25rem 0.5rem;
    border-radius: 10px;
  }
`;

/**
 * ✅ Sticky footer (móvil) para que el botón sea siempre accesible
 */
const BottomActions = styled.div`
  &[data-mobile='true'] {
    position: sticky;
    bottom: 0;
    padding: 0.75rem 0.75rem calc(0.75rem + env(safe-area-inset-bottom));
    background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.65) 30%, rgba(0, 0, 0, 0.75) 100%);
    backdrop-filter: blur(10px);
    box-sizing: border-box;
  }
`;

const SongSelectionButton = styled(Button) <{ focused?: boolean }>`
  position: absolute;
  bottom: 13rem;
  right: 2rem;
  padding: 0 3rem;
  font-size: 1.9vw;

  /* ✅ Mobile */
  &[data-mobile='true'] {
    position: relative;
    bottom: auto;
    right: auto;
    width: 100%;
    font-size: clamp(18px, 5vw, 28px);
    border-radius: 18px;
    padding: 1rem 1.25rem;
  }
`;